/**
 * PlayerAugmentationsPanel Component
 * Displays active augmentations for both players
 * Uses inline styles for consistent appearance
 */

import { getAugmentation } from "@cloudnest/augmented-chess-engine";
import type { PlayerAugmentationsPanelProps } from "../../types";

/** Styles for the panel container */
const panelStyles: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  width: "100%",
  maxWidth: "512px",
  marginBottom: "16px",
  gap: "16px",
};

/** Styles for a player's augmentation section */
const playerSectionStyles: React.CSSProperties = {
  flex: 1,
  backgroundColor: "#fff",
  borderRadius: "8px",
  padding: "12px",
  border: "1px solid #e0e0e0",
  boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
};

/** Styles for the player header */
const playerHeaderStyles: React.CSSProperties = {
  margin: "0 0 8px 0",
  fontSize: "0.9rem",
  fontWeight: "bold",
  display: "flex",
  alignItems: "center",
  gap: "6px",
};

/** Styles for the augmentation list */
const listStyles: React.CSSProperties = {
  listStyle: "none",
  margin: 0,
  padding: 0,
};

/** Styles for an augmentation item */
const itemStyles: React.CSSProperties = {
  fontSize: "0.8rem",
  padding: "6px 8px",
  backgroundColor: "#f8f8f8",
  borderRadius: "4px",
  marginBottom: "4px",
  display: "flex",
  flexDirection: "column",
  border: "1px solid #eee",
};

/** Styles for the augmentation name */
const nameStyles: React.CSSProperties = {
  fontWeight: "bold",
  color: "#333",
};

/** Styles for the augmentation description */
const descriptionStyles: React.CSSProperties = {
  fontSize: "0.7rem",
  color: "#666",
  marginTop: "2px",
};

/** Styles for no augmentations message */
const emptyStyles: React.CSSProperties = {
  fontSize: "0.8rem",
  color: "#999",
  fontStyle: "italic",
};

/** Props for AugmentationList */
interface AugmentationListProps {
  title: string;
  augIds: string[];
  color: "WHITE" | "BLACK";
}

/** List of augmentations for a single player */
function AugmentationList({
  title,
  augIds,
  color,
}: AugmentationListProps): JSX.Element {
  const augmentations = augIds
    .map((id) => getAugmentation(id))
    .filter((aug) => aug !== undefined);

  const headerColor = color === "WHITE" ? "#333" : "#555";
  const indicator = color === "WHITE" ? "\u2654" : "\u265A"; // King unicode

  return (
    <div style={playerSectionStyles}>
      <h4 style={{ ...playerHeaderStyles, color: headerColor }}>
        <span>{indicator}</span>
        {title}
      </h4>
      {augmentations.length === 0 ? (
        <p style={emptyStyles}>증강 없음</p>
      ) : (
        <ul style={listStyles}>
          {augmentations.map((aug) => (
            <li
              key={aug!.id}
              style={itemStyles}
              title={aug!.descriptionKo ?? aug!.description}
            >
              <span style={nameStyles}>{aug!.nameKo ?? aug!.name}</span>
              <span style={descriptionStyles}>
                {aug!.descriptionKo ?? aug!.description}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Main player augmentations panel component */
export function PlayerAugmentationsPanel({
  gameState,
  whiteAugmentations,
  blackAugmentations,
}: PlayerAugmentationsPanelProps): JSX.Element | null {
  // Don't show if still in selection phase
  if (gameState.phase === "AUGMENTATION_SELECTION") {
    return null;
  }

  return (
    <div style={panelStyles}>
      <AugmentationList title="백" augIds={whiteAugmentations} color="WHITE" />
      <AugmentationList title="흑" augIds={blackAugmentations} color="BLACK" />
    </div>
  );
}
